/**
 * Editable tree flow for the Uncheck use case.
 * Allows adding/removing nodes and edges, changing logical connectors, and editing node text.
 * Changes are persisted via the updateProcessedClauseTree API.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LogicalOperator,
  Outcome,
} from "@financial-advisory/data-access/api-types";
import type { ConditionNode } from "@financial-advisory/data-access/api-types";
import { SecondaryButton } from "@financial-advisory/ui/components/buttons";
import {
  Background,
  Controls,
  Edge,
  Node,
  Panel,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useOnSelectionChange,
} from "@xyflow/react";
import type { Connection } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Input, Select } from "antd";
import { v4 as uuid } from "uuid";
import {
  ConditionNode as ConditionNodeComponent,
  CustomEdge,
  LogicalConnectiveNode,
  RFNodeData,
  conditionNodeToRepresentation,
  convertToRF,
  getLayoutedElements,
  rfNodesEdgesToConditionNode,
} from "@unlikelyai-magic/shared/components";
import { cn } from "@unlikelyai-magic/utils";

const edgeTypes = { "custom-edge": CustomEdge };

const nodeTypes = {
  connective: LogicalConnectiveNode,
  condition: ConditionNodeComponent,
};

export interface EditableTreeFlowProps {
  /** Initial tree root (from pane's definition or rule tree). */
  initialRootNode: ConditionNode | Record<string, unknown>;
  paneId: string;
  treeKind: "trigger" | "obligations" | "definition";
  productTreeId: string;
  regulationId: string;
  processedClauseId: string;
  onSave: () => void;
  onCancel: () => void;
  saveTree: (params: {
    regulationId: string;
    processedClauseId: string;
    paneId: string;
    treeKind: "trigger" | "obligations" | "definition";
    rootNode: ConditionNode;
  }) => Promise<unknown>;
  className?: string;
}

export function EditableTreeFlow({
  initialRootNode,
  paneId,
  treeKind,
  productTreeId,
  regulationId,
  processedClauseId,
  onSave,
  onCancel,
  saveTree,
  className,
}: EditableTreeFlowProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editOperator, setEditOperator] = useState<LogicalOperator | null>(
    null
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialNodesAndEdges = useMemo(() => {
    const rep = conditionNodeToRepresentation(initialRootNode as ConditionNode);
    const { nodes, edges } = convertToRF(productTreeId, rep);
    return { nodes, edges };
  }, [initialRootNode, productTreeId]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<RFNodeData>>(
    initialNodesAndEdges.nodes as Node<RFNodeData>[]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    initialNodesAndEdges.edges
  );

  const isNodesInitialized = useNodesInitialized();
  const [isInitialLayoutDone, setIsInitialLayoutDone] = useState(false);

  const runLayout = useCallback(async () => {
    const layouted = await getLayoutedElements(
      nodes as Node<RFNodeData>[],
      edges,
      { direction: "TB" }
    );
    setNodes(layouted.nodes as Node<RFNodeData>[]);
    setEdges(layouted.edges);
  }, [nodes, edges, setNodes, setEdges]);

  const runLayoutRef = useRef(runLayout);
  runLayoutRef.current = runLayout;

  // Run initial layout only after nodes are measured (like LayoutFlow). Use a ref for runLayout
  // so this effect does not re-fire when nodes/edges change (which would cause a redundant layout).
  useEffect(() => {
    if (!isNodesInitialized || isInitialLayoutDone || nodes.length === 0)
      return;
    const timeoutId = setTimeout(() => {
      void runLayoutRef.current?.().then(() => setIsInitialLayoutDone(true));
    }, 10);
    return () => clearTimeout(timeoutId);
  }, [isNodesInitialized, isInitialLayoutDone, nodes.length]);

  useOnSelectionChange({
    onChange: ({ nodes: selectedNodes }) => {
      const n = selectedNodes[0];
      if (!n) {
        setSelectedNodeId(null);
        setEditQuestion("");
        setEditOperator(null);
        return;
      }
      setSelectedNodeId(n.id);
      const d = n.data as RFNodeData;
      setEditQuestion(d.question ?? d.label ?? "");
      const isParent = edges.some((e) => e.source === n.id);
      setEditOperator(
        n.type === "connective" || isParent
          ? ((d.logicalOperator as LogicalOperator) ?? LogicalOperator.AND)
          : null
      );
    },
  });

  const selectedNode = useMemo(
    () => (selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null),
    [nodes, selectedNodeId]
  );

  const applySelectedNodeEdit = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== selectedNodeId) return n;
        const data = n.data as RFNodeData;
        return {
          ...n,
          data: {
            ...data,
            question: editQuestion || data.question,
            label: editQuestion || data.label,
            ...(editOperator != null ? { logicalOperator: editOperator } : {}),
          },
        };
      })
    );
  }, [selectedNodeId, editQuestion, editOperator, setNodes]);

  const handleAddChild = useCallback(async () => {
    if (!selectedNodeId) return;
    const newId = uuid();
    const newNode: Node<RFNodeData> = {
      id: newId,
      type: "condition",
      position: { x: 0, y: 0 },
      parentId: productTreeId,
      data: {
        productTreeId,
        label: `New condition ${newId.slice(0, 6)}`,
        question: `New condition ${newId.slice(0, 6)}`,
        value: Outcome.UNDECIDED,
        logicalOperator: LogicalOperator.AND,
      },
    };
    const newEdge: Edge = {
      id: `${selectedNodeId}-${newId}`,
      type: "custom-edge",
      source: selectedNodeId,
      target: newId,
      animated: true,
    };
    // When adding a child, the parent becomes a connective node so it shows the logical-operator UI
    const updatedNodes = nodes.map((n) =>
      n.id === selectedNodeId
        ? {
            ...n,
            type: "connective" as const,
            data: {
              ...(n.data as RFNodeData),
              logicalOperator:
                (n.data as RFNodeData).logicalOperator ?? LogicalOperator.AND,
            },
          }
        : n
    ) as Node<RFNodeData>[];
    const nextNodes = [...updatedNodes, newNode];
    const nextEdges = [...edges, newEdge];
    const layouted = await getLayoutedElements(nextNodes, nextEdges, {
      direction: "TB",
    });
    setNodes(layouted.nodes as Node<RFNodeData>[]);
    setEdges([...layouted.edges]);
  }, [selectedNodeId, nodes, edges, productTreeId, setNodes, setEdges]);

  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    const removeIds = new Set<string>([selectedNodeId]);
    const seen = new Set<string>();
    const collect = (id: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      edges.forEach((e) => {
        if (e.source === id) {
          removeIds.add(e.target);
          collect(e.target);
        }
      });
    };
    collect(selectedNodeId);
    const nextEdges = edges.filter(
      (e) => !removeIds.has(e.source) && !removeIds.has(e.target)
    );
    const remainingSourceIds = new Set(nextEdges.map((e) => e.source));
    setNodes((nds) =>
      nds
        .filter((n) => !removeIds.has(n.id))
        .map((n) => {
          // Downgrade to leaf when a connective node has no children left
          if (n.type === "connective" && !remainingSourceIds.has(n.id)) {
            return {
              ...n,
              type: "condition" as const,
              data: {
                ...(n.data as RFNodeData),
                value: Outcome.UNDECIDED,
              },
            };
          }
          return n;
        })
    );
    setEdges(nextEdges);
    setSelectedNodeId(null);
    setEditQuestion("");
    setEditOperator(null);
  }, [selectedNodeId, edges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const handleSave = useCallback(async () => {
    setSaveError(null);
    applySelectedNodeEdit();
    // Build tree from nodes with the selected node's edits applied inline, so we don't rely on
    // stale state (setNodes is async; nodes would still be pre-edit here).
    const appliedNodes =
      selectedNodeId == null
        ? nodes
        : nodes.map((n) => {
            if (n.id !== selectedNodeId) return n;
            const data = n.data as RFNodeData;
            return {
              ...n,
              data: {
                ...data,
                question: editQuestion || data.question,
                label: editQuestion || data.label,
                ...(editOperator != null
                  ? { logicalOperator: editOperator }
                  : {}),
              },
            };
          });
    const treeNodes = appliedNodes.filter((n) => n.type !== "group");
    const treeEdges = edges.filter(
      (e) =>
        treeNodes.some((n) => n.id === e.source) &&
        treeNodes.some((n) => n.id === e.target)
    );
    const targetIds = new Set(treeEdges.map((e) => e.target));
    const rootId = treeNodes.find((n) => !targetIds.has(n.id))?.id;
    if (!rootId) {
      setSaveError("Tree is empty or invalid; add at least one node to save.");
      return;
    }
    const rootNode = rfNodesEdgesToConditionNode(rootId, treeNodes, treeEdges);
    if (!rootNode) {
      setSaveError("Tree is empty or invalid; add at least one node to save.");
      return;
    }
    setSaveError(null);
    try {
      await saveTree({
        regulationId,
        processedClauseId,
        paneId,
        treeKind,
        rootNode: rootNode as unknown as ConditionNode,
      });
      onSave();
    } catch (err) {
      let message = "Failed to save tree";
      if (err instanceof Error) {
        message = err.message;
      } else if (err && typeof err === "object" && "data" in err) {
        const data = (err as { data?: { detail?: unknown } }).data;
        const detail = data?.detail;
        if (typeof detail === "string") {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail
            .map((e) =>
              e && typeof e === "object" && "msg" in e
                ? String((e as { msg: unknown }).msg)
                : String(e)
            )
            .join("; ");
        } else if (detail != null) {
          message = JSON.stringify(detail);
        }
      }
      setSaveError(message);
    }
  }, [
    nodes,
    edges,
    selectedNodeId,
    editQuestion,
    editOperator,
    applySelectedNodeEdit,
    regulationId,
    processedClauseId,
    paneId,
    treeKind,
    saveTree,
    onSave,
  ]);

  const hasChildren = selectedNodeId
    ? edges.some((e) => e.source === selectedNodeId)
    : false;
  const isConnective =
    selectedNode?.type === "connective" ||
    (selectedNode != null && hasChildren);

  // Keyboard Delete/Backspace: use same logic as Delete button (downgrade parent, reset value)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!selectedNodeId) return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;
      e.preventDefault();
      handleDeleteNode();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedNodeId, handleDeleteNode]);

  return (
    <div className={cn("w-full h-full flex flex-col", className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={[]}
        nodesDraggable
        nodesConnectable
        elementsSelectable
        minZoom={0.01}
        defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
      >
        <Panel
          position="top-right"
          className="flex flex-col gap-2 bg-white p-2 rounded border shadow max-w-xs"
        >
          <div className="text-sm font-medium text-gray-700">Edit tree</div>
          {selectedNode ? (
            <>
              <div>
                <label className="text-xs text-gray-500">Text</label>
                <Input
                  value={editQuestion}
                  onChange={(e) => setEditQuestion(e.target.value)}
                  onBlur={applySelectedNodeEdit}
                  placeholder="Node text"
                />
              </div>
              {isConnective && (
                <div>
                  <label className="text-xs text-gray-500">Connector</label>
                  <Select
                    value={editOperator ?? LogicalOperator.AND}
                    onChange={(v) => setEditOperator(v as LogicalOperator)}
                    onBlur={applySelectedNodeEdit}
                    className="w-full"
                    options={[
                      { value: LogicalOperator.AND, label: "AND" },
                      { value: LogicalOperator.OR, label: "OR" },
                      { value: LogicalOperator.NOT, label: "NOT" },
                    ]}
                  />
                </div>
              )}
              <div className="flex gap-1">
                <SecondaryButton
                  onClick={handleAddChild}
                  title="Add child node"
                >
                  Add child
                </SecondaryButton>
                <SecondaryButton onClick={handleDeleteNode} title="Remove node">
                  Delete
                </SecondaryButton>
              </div>
            </>
          ) : (
            <span className="text-xs text-gray-500">Select a node to edit</span>
          )}
          {saveError && (
            <div className="text-xs text-red-600 border border-red-200 rounded p-1.5 bg-red-50">
              {saveError}
            </div>
          )}
          <div className="flex gap-1 border-t pt-2 mt-2">
            <SecondaryButton onClick={handleSave} title="Save to server">
              Save
            </SecondaryButton>
            <SecondaryButton
              onClick={() => {
                setSaveError(null);
                onCancel();
              }}
              title="Exit edit mode"
            >
              Cancel
            </SecondaryButton>
          </div>
        </Panel>
        <Controls />
        <Background gap={12} size={1} />
      </ReactFlow>
    </div>
  );
}
